import type {
  Building,
  Core,
  EntityId,
  MatchConnection,
  MatchLifecycleEvent,
  MatchState,
  PlayerId,
  Projectile,
  Squad,
  SquadArchetype,
  Unsubscribe,
} from '@signalclash/shared';
import { isOperational } from '@signalclash/shared';

import type { AudioBus } from './AudioBus.js';
import type { Voice } from './Voice.js';

// Derives sketch SFX from sim deltas + lifecycle events. Audio is presentational —
// these events are NOT part of MatchState (Rule 1) — so we diff snapshots client-side
// and emit "events" to the AudioBus. Throttled per name so a tick that fires three
// projectiles doesn't blast three identical clicks on the same frame.
//
// V1+ variant rotation: events that fire frequently (projectile_hit, squad_death)
// pick a `name_1`/`name_2`/`name_3` variant per call so the player doesn't hear
// the same sample on a tight loop. The squad-select / order-move / order-attack
// click SFX were removed when the V1 voice pack shipped — `voice_*_select_*` /
// `voice_*_move_*` / `voice_*_attack_*` replace them, archetype-routed by
// `Voice.ts`. Enemy/neutral squad-event SFX (squad_spawned, squad_death_*) stay
// because voice only narrates viewer-owned events.
//
// V1+ per-source differentiation:
//   - projectile_fire branches on the projectile's source (squad archetype OR
//     building kind). Squad → recon_fire/assault_fire/engineer_fire/shield_fire.
//     Permanent turret → turret_perm_fire; mini_turret → turret_fire. Anything
//     else falls back to the generic projectile_fire.
//   - projectile_hit branches on target kind (building/core → building_hit_*;
//     squad/neutral_unit → unit_hit_*).
//
// V1+ Lifecycle, UI, and announcer hooks:
//   match_start / overtime_started / signal_surge_started / doctrine_window
//   ui_click / ui_hover / ui_error / ui_select_{squad,building,core} /
//   ui_order_{move,attack} / ui_build_open
//   announce_kill / multikill_2 / multikill_3 / multikill_4 / multikill_god
//   timer_warning (60 s remaining) / final_countdown (30 s remaining)
//   relay_lost (viewer-owned relay flipped to enemy)
//   squad_spawned (new squad pops from a Hub)
//   ability_mark_target / ability_shield_on / ability_shield_off
const PER_NAME_THROTTLE_MS = 40;

const FIRE_VARIANT_BY_ARCHETYPE: Record<SquadArchetype, string> = {
  recon: 'recon_fire',
  assault: 'assault_fire',
  engineer: 'engineer_fire',
  shield: 'shield_fire',
};

const UNIT_HIT_VARIANTS = 4;
const BUILDING_HIT_VARIANTS = 6;
const SQUAD_DEATH_VARIANTS = 3;

// Multi-kill window: deaths of enemy squads attributed to the viewer within
// this many ms stack onto a streak. Tuned so a clean 2-squad wipe in a single
// exchange registers, but two separate skirmishes don't bleed into one streak.
const MULTIKILL_WINDOW_MS = 5000;

// Final-minute thresholds in match seconds REMAINING.
const TIMER_WARNING_REMAINING_SEC = 60;
const FINAL_COUNTDOWN_REMAINING_SEC = 30;

// ─── Voice trigger thresholds ─────────────────────────────────────────────
// These are read-only inputs to the voice layer and live next to the SFX
// constants because Sfx is the snapshot-diff site. Tuning happens here.
//
// Core integrity critical fires once when viewer core HP ratio drops below
// this fraction. Per V1 spec: < 40% is "critical".
const VOICE_CORE_CRITICAL_RATIO = 0.4;
// Income-increased voice fires when viewer income increases by at least this
// much (signal per second) since the last fire. Avoids triggering on tiny
// flip-rate fluctuations from a single relay losing its connection mid-flip.
const VOICE_INCOME_DELTA_THRESHOLD = 1.0;
// Snapshot window used to count recent viewer squad deaths for the
// "defense line weakening" heuristic and the multi-death announcer.
const RECENT_DEATH_WINDOW_MS = 8000;
const DEFENSE_WEAKENING_DEATH_COUNT = 2;
// Radius around the viewer core inside which an enemy squad counts as
// "approaching core". 600 px is roughly the same scale as the existing fog
// reveal radius — close enough to be a real threat, far enough to warn the
// player before they're already at the gates. Map widths run 2000–3000 px.
const HOSTILES_APPROACHING_CORE_RADIUS = 600;
// Radius around any viewer-owned relay used by the "enemy push incoming"
// detector. Larger than the core proximity because we want to surface the
// push earlier, while the player still has options.
const ENEMY_PUSH_RELAY_RADIUS = 800;
const ENEMY_PUSH_THRESHOLD_COUNT = 3;

// Combat cues that benefit from per-call pitch/volume jitter. Anything that
// fires repeatedly in quick succession lives here; one-shots and UI clicks
// don't (they need to sound identical across plays for player trust).
function isCombatSfx(name: string): boolean {
  return (
    name.endsWith('_fire') ||
    name.startsWith('building_hit_') ||
    name.startsWith('unit_hit_') ||
    name.startsWith('squad_death_')
  );
}

// Per-cue volume scalars for SFX that are intrinsically too loud at master
// level. These are NOT preference tweaks; they correct samples that drown
// out everything else when they fire (explosions, brass announcer stings).
// Replacing the samples is the long-term fix; until then, this keeps the
// mix from biting the player.
const SFX_VOLUME_OVERRIDES: Record<string, number> = {
  building_destroyed: 0.385,
  announce_kill: 0.5,
  multikill_2: 0.45,
  multikill_3: 0.45,
  multikill_4: 0.45,
  multikill_god: 0.45,
};

export class Sfx {
  private prev: MatchState | null = null;
  private unsubSnap: Unsubscribe | null = null;
  private unsubLife: Unsubscribe | null = null;
  private lastPlayedAt = new Map<string, number>();
  private muted = false;
  // Per-family rotation counters so consecutive calls rotate through the
  // available variants rather than hitting Math.random() (which would still
  // sometimes repeat the same variant twice in a row).
  private unitHitRotation = 0;
  private buildingHitRotation = 0;
  private squadDeathRotation = 0;
  // Multi-kill streak tracking. `pendingKillCount` is the number of enemy-squad
  // kills landed within the last MULTIKILL_WINDOW_MS; `killWindowAt` is when
  // the window opened (refreshed on each kill). When the window expires we
  // emit the streak announce; new kills after that start fresh.
  private pendingKillCount = 0;
  private killWindowAt = 0;
  private killWindowTimer: ReturnType<typeof setTimeout> | null = null;
  // One-shot timer cues: the warning fires ONCE per match when the clock first
  // drops past the threshold; the snapshot diff is the only fire site.
  private timerWarningEmitted = false;
  private finalCountdownEmitted = false;
  // ─── Voice trigger state ────────────────────────────────────────────────
  // Per-viewer-squad HP cache for under-fire detection. Only viewer-owned
  // squads are tracked; an enemy squad taking damage isn't player-personal
  // enough to warrant a voice line.
  private viewerSquadPrevHp = new Map<EntityId, number>();
  // Viewer core HP cache for core-damage / core-critical detection.
  private viewerCorePrevHp = new Map<EntityId, number>();
  // Recent viewer squad deaths (timestamps). Used for the multi-death
  // generic "Squad lost" announcer and the defense-line-weakening heuristic.
  private recentViewerDeaths: number[] = [];
  // Viewer income high-water mark. Voice fires only when income climbs by at
  // least VOICE_INCOME_DELTA_THRESHOLD above this mark.
  private viewerIncomeMark = 0;
  // Set to true after the first diffIncome call so the initial 0→base jump
  // (the starting PlayerState always emits signalIncomePerSec=0 before the
  // first economy tick) silently calibrates the mark instead of firing.
  private viewerIncomeSeeded = false;
  // Defense-line cadence — track timestamps that count as "pressure". When
  // pressure events haven't fired for a while, the holding line becomes
  // eligible (gated additionally by Voice's own cooldown).
  private lastPressureAt = 0;
  // Voice handle — set via setVoice() from ClientApp. Optional so existing
  // tests / construction paths that don't wire voice keep compiling.
  private voice: Voice | null = null;
  // Match index, threaded in from ClientApp so match-start voice rotation
  // can vary the cue per match without leaking state across rematches.
  private matchIndex = 0;

  constructor(
    private readonly bus: AudioBus,
    private viewerId: PlayerId,
  ) {}

  setVoice(voice: Voice): void {
    this.voice = voice;
  }

  // Called from ClientApp before each match starts so the match-start
  // announcer rotation picks a different cue across rematches.
  setMatchIndex(index: number): void {
    this.matchIndex = index;
  }

  setPlayerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
  }

  attach(connection: MatchConnection, onLifecycle: (h: (e: MatchLifecycleEvent) => void) => Unsubscribe): void {
    this.detach();
    this.unsubSnap = connection.onSnapshot((snap) => this.onSnapshot(snap));
    this.unsubLife = onLifecycle((e) => this.onLifecycle(e));
  }

  detach(): void {
    this.unsubSnap?.();
    this.unsubSnap = null;
    this.unsubLife?.();
    this.unsubLife = null;
    if (this.killWindowTimer !== null) {
      clearTimeout(this.killWindowTimer);
      this.killWindowTimer = null;
    }
    this.pendingKillCount = 0;
    this.killWindowAt = 0;
    this.timerWarningEmitted = false;
    this.finalCountdownEmitted = false;
    this.viewerSquadPrevHp.clear();
    this.viewerCorePrevHp.clear();
    this.recentViewerDeaths = [];
    this.viewerIncomeMark = 0;
    this.viewerIncomeSeeded = false;
    this.lastPressureAt = 0;
    this.voice?.resetForMatch();
    this.prev = null;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  // ─── Public UI / selection / order entry points ────────────────────────────
  // Callers in UI surfaces (menu buttons, InputRouter, BuildPanel) drive these
  // directly. Selection / order sounds aren't derivable from snapshot deltas —
  // they're per-input feedback that only the client knows happened.

  playUiClick(): void {
    this.play('ui_click');
  }

  playUiHover(): void {
    this.play('ui_hover');
  }

  playUiError(): void {
    this.play('ui_error');
  }

  playUiBuildOpen(): void {
    this.play('ui_build_open');
  }

  // Selection sound dispatched by reference kind. Buildings and Cores still
  // play a click SFX; squad selection is now narrated by the V1 voice pack
  // (archetype-routed `voice_*_select_*`) so the generic squad-click was
  // removed to avoid layering two acknowledgements over the same input.
  playSelection(kind: 'squad' | 'building' | 'core', archetype?: SquadArchetype): void {
    switch (kind) {
      case 'squad': {
        if (archetype !== undefined) this.voice?.playSelection(archetype);
        return;
      }
      case 'building':
        this.play('ui_select_building');
        return;
      case 'core':
        this.play('ui_select_core');
        return;
    }
  }

  // Move / attack orders are voice-only since the V1 pack landed — the
  // generic order SFX clicks (`ui_order_move` / `ui_order_attack`) were
  // removed so the player hears the per-class voice line, not two stacked
  // cues. `hapticTap()` on the InputRouter side still gives the physical-
  // feel feedback on touch devices.
  playOrderMove(archetype?: SquadArchetype): void {
    if (archetype !== undefined) this.voice?.playMoveOrder(archetype);
  }

  playOrderAttack(archetype?: SquadArchetype): void {
    if (archetype !== undefined) this.voice?.playAttackOrder(archetype);
  }

  // ─── Snapshot diff loop ────────────────────────────────────────────────────

  private onSnapshot(snap: MatchState): void {
    if (this.prev === null) {
      // Seed the viewer-squad HP cache from the first tick so under-fire
      // detection doesn't false-fire on the second tick from "0 → real HP".
      for (const s of Object.values(snap.squads)) {
        if (s.ownerId === this.viewerId) this.viewerSquadPrevHp.set(s.id, s.hp);
      }
      for (const c of Object.values(snap.cores)) {
        if (c.ownerId === this.viewerId) this.viewerCorePrevHp.set(c.id, c.hp);
      }
      const viewer = this.viewerPlayerIndex(snap);
      if (viewer !== null) this.viewerIncomeMark = snap.players[viewer].signalIncomePerSec;
      this.prev = snap;
      return;
    }
    this.diffBuildings(this.prev, snap);
    this.diffProjectiles(this.prev, snap);
    this.diffSquads(this.prev, snap);
    this.diffCaptures(this.prev, snap);
    this.diffShieldBubbles(this.prev, snap);
    this.diffCores(snap);
    this.diffIncome(snap);
    this.checkThreatHeuristics(snap);
    this.checkDefenseLineHolding();
    this.checkTimerThresholds(snap);
    this.prev = snap;
  }

  private onLifecycle(e: MatchLifecycleEvent): void {
    if (e.kind === 'match_ended') {
      if (e.outcome.winner === this.viewerId) {
        this.play('victory');
        void this.bus.playMusic('jingle_victory', { loop: false, fadeMs: 200 });
        this.voice?.playMatchVictory();
      } else {
        this.play('defeat');
        void this.bus.playMusic('jingle_defeat', { loop: false, fadeMs: 200 });
        // Hard defeat (own core destroyed) uses the rarer SIGNAL_LOST line so
        // it reads distinct from a dominance / overtime loss.
        const coreDestroyed =
          e.outcome.kind === 'core_destroyed' && e.outcome.loser === this.viewerId;
        this.voice?.playMatchDefeat({ coreDestroyed });
      }
      return;
    }
    if (e.kind === 'started') {
      this.play('match_start');
      this.voice?.playMatchStart(this.matchIndex);
      return;
    }
    if (e.kind === 'overtime_started') {
      this.play('overtime_started');
      return;
    }
    if (e.kind === 'signal_surge_started') {
      this.play('signal_surge_started');
      return;
    }
    if (e.kind === 'doctrine_window_open') {
      this.play('doctrine_window');
      return;
    }
    if (e.kind === 'signal_storm_started') {
      this.play('signal_storm_started');
      return;
    }
    if (e.kind === 'critical_flip') {
      switch (e.reason) {
        case 'link_cut':
          this.play('link_cut');
          return;
        case 'reconnected':
          this.play('link_restored');
          return;
        case 'relay_isolated':
          this.play('relay_isolated');
          return;
        case 'mini_turret_dark':
          this.play('turret_offline');
          return;
        case 'relay_jammed':
          this.play('relay_jammed');
          return;
        case 'uplink_disrupted':
          this.play('uplink_disrupted');
          return;
        case 'relay_fortified':
          this.play('relay_fortified');
          return;
      }
    }
  }

  private diffBuildings(prev: MatchState, next: MatchState): void {
    for (const [idStr, b] of Object.entries(next.buildings)) {
      const id = idStr as unknown as EntityId;
      const before: Building | undefined = prev.buildings[id];
      if (before === undefined) {
        this.play('build_start');
        continue;
      }
      if (!isOperational(before) && isOperational(b)) {
        this.play('build_complete');
      }
    }
    for (const [idStr, before] of Object.entries(prev.buildings)) {
      const id = idStr as unknown as EntityId;
      if (next.buildings[id] === undefined && isOperational(before)) {
        this.play('building_destroyed');
      }
    }
  }

  private diffProjectiles(prev: MatchState, next: MatchState): void {
    for (const [idStr, projectile] of Object.entries(next.projectiles)) {
      const id = idStr as unknown as EntityId;
      if (prev.projectiles[id] === undefined) {
        this.play(this.pickFireCue(projectile, next));
      }
    }
    for (const [idStr, projectile] of Object.entries(prev.projectiles)) {
      const id = idStr as unknown as EntityId;
      if (next.projectiles[id] === undefined) {
        this.play(this.pickHitCue(projectile));
      }
    }
  }

  // Picks a fire cue based on which entity spawned the projectile. Squad
  // archetypes get their own per-class cue; building kinds branch by kind
  // (turret vs mini_turret); anything else falls back to projectile_fire.
  private pickFireCue(projectile: Projectile, snap: MatchState): string {
    if (projectile.fromSquadId !== null) {
      const squad: Squad | undefined = snap.squads[projectile.fromSquadId];
      if (squad !== undefined) {
        return FIRE_VARIANT_BY_ARCHETYPE[squad.archetype] ?? 'projectile_fire';
      }
    }
    if (projectile.fromBuildingId !== null) {
      const building: Building | undefined = snap.buildings[projectile.fromBuildingId];
      if (building !== undefined) {
        if (building.kind === 'turret') return 'turret_perm_fire';
        if (building.kind === 'mini_turret') return 'turret_fire';
      }
    }
    return 'projectile_fire';
  }

  private pickHitCue(projectile: Projectile): string {
    switch (projectile.target.kind) {
      case 'building':
      case 'core': {
        this.buildingHitRotation = (this.buildingHitRotation + 1) % BUILDING_HIT_VARIANTS;
        return `building_hit_${this.buildingHitRotation + 1}`;
      }
      case 'squad':
      case 'neutral_unit': {
        this.unitHitRotation = (this.unitHitRotation + 1) % UNIT_HIT_VARIANTS;
        return `unit_hit_${this.unitHitRotation + 1}`;
      }
    }
  }

  private diffSquads(prev: MatchState, next: MatchState): void {
    // New squads = production tick. Pre-existing squads on the first diff are
    // handled by the `prev === null` early-return in `onSnapshot`, so this
    // only fires on actual hub production.
    for (const [idStr, sq] of Object.entries(next.squads)) {
      const id = idStr as unknown as EntityId;
      if (prev.squads[id] === undefined) {
        this.play('squad_spawned');
        // Voice: only narrate the player's own production. Cooldown-gated so
        // a Hub double-tick doesn't spam "Squad ready" twice in a row.
        if (sq.ownerId === this.viewerId) this.voice?.playSquadReady();
      }
    }
    // Under-fire detection — runs over the next.squads table so newly-spawned
    // squads at half-HP (shouldn't happen in practice, but safe) don't false
    // trigger. Only viewer-owned squads are tracked. We compare HP against
    // the previous cached value, not against prev.squads (which would also
    // fire on the next tick after the squad just spawned at full HP).
    for (const sq of Object.values(next.squads)) {
      if (sq.ownerId !== this.viewerId) continue;
      const prevHp = this.viewerSquadPrevHp.get(sq.id);
      if (prevHp !== undefined && sq.hp < prevHp) {
        this.voice?.playUnderFire(sq.archetype, sq.id as unknown as number);
      }
      this.viewerSquadPrevHp.set(sq.id, sq.hp);
    }
    // Death — variant rotation + multi-kill attribution to the viewer when
    // an enemy squad dies. Viewer's own squad dying just plays the death
    // variant; we don't run that through the streak counter so the enemy
    // doesn't get credit for the viewer's losses on the viewer's mix.
    //
    // Voice: viewer-owned deaths route to the archetype-specific lost voice.
    // The generic SQUAD_LOST line only lands when several viewer squads die
    // inside RECENT_DEATH_WINDOW_MS — otherwise it would always step on the
    // archetype line.
    const now = performance.now();
    for (const [idStr, before] of Object.entries(prev.squads)) {
      const id = idStr as unknown as EntityId;
      if (next.squads[id] === undefined) {
        this.squadDeathRotation = (this.squadDeathRotation + 1) % SQUAD_DEATH_VARIANTS;
        this.play(`squad_death_${this.squadDeathRotation + 1}`);
        if (before.ownerId !== this.viewerId) {
          this.registerViewerKill();
        } else {
          // Drop the HP cache entry so a dead squad doesn't gate a future
          // under-fire trigger for a recycled entity id.
          this.viewerSquadPrevHp.delete(id);
          this.voice?.playSquadLost(before.archetype);
          this.recordViewerDeath(now);
          if (this.countRecentDeaths(now) >= 3) {
            // Three viewer deaths inside the window — surface the generic
            // announcer line over the archetype-specific one. Voice queue
            // gates this against cooldown so it doesn't fire on every tick
            // of a wipe.
            this.voice?.playMultipleSquadLost();
          }
          if (this.countRecentDeaths(now) >= DEFENSE_WEAKENING_DEATH_COUNT) {
            this.voice?.playDefenseLineWeakening();
            this.lastPressureAt = now;
          }
        }
      }
    }
  }

  private diffCaptures(prev: MatchState, next: MatchState): void {
    for (const [idStr, node] of Object.entries(next.controlNodes)) {
      const id = idStr as unknown as EntityId;
      const before = prev.controlNodes[id];
      if (before === undefined) continue;
      // Viewer-owned relay flipped to enemy = relay_lost; lands underneath the
      // generic capture_complete cue we still play below so both layers fire
      // (lost is the personal sting, capture_complete is the world event).
      if (before.ownerId === this.viewerId && node.ownerId !== null && node.ownerId !== this.viewerId) {
        this.play('relay_lost');
        // Losing a relay is "pressure" — feeds defense_line_weakening eligibility
        // and resets the holding-line clock.
        this.lastPressureAt = performance.now();
        this.voice?.playDefenseLineWeakening();
      }
      if (before.ownerId === null && node.ownerId !== null) {
        this.play('capture_complete');
      } else if (before.ownerId !== null && node.ownerId !== null && before.ownerId !== node.ownerId) {
        this.play('capture_complete');
      }
    }
  }

  // Viewer-core HP tracking. Voice routing:
  //   - HP decrease → CORE_UNDER_ATTACK / SIGNAL_CORE_TAKING_DAMAGE rotation
  //     (Voice handles the cooldown so per-projectile-hit spam can't land).
  //   - HP ratio crosses VOICE_CORE_CRITICAL_RATIO → CORE_INTEGRITY_CRITICAL
  //     fires once per match at CRITICAL priority and interrupts anything else.
  // Only the viewer's own core is tracked — enemy core damage isn't player-
  // personal and would step on the player's narrative beats.
  private diffCores(next: MatchState): void {
    for (const c of Object.values(next.cores)) {
      if (c.ownerId !== this.viewerId) continue;
      const prevHp = this.viewerCorePrevHp.get(c.id);
      if (prevHp !== undefined && c.hp < prevHp) {
        this.voice?.playCoreDamage();
        this.lastPressureAt = performance.now();
      }
      if (c.maxHp > 0 && c.hp / c.maxHp < VOICE_CORE_CRITICAL_RATIO) {
        this.voice?.playCoreIntegrityCritical();
      }
      this.viewerCorePrevHp.set(c.id, c.hp);
    }
    // Drop cache entries for cores that no longer exist (post-destruction).
    for (const id of [...this.viewerCorePrevHp.keys()]) {
      if (next.cores[id] === undefined) this.viewerCorePrevHp.delete(id);
    }
  }

  // Viewer income tracking. Fires SIGNAL_INCOME_INCREASED only when income
  // climbs by at least VOICE_INCOME_DELTA_THRESHOLD over the most recent
  // "marked" income level. The mark is bumped on every fire so the line
  // doesn't keep retriggering at the same plateau, and is also bumped down
  // if income falls back so a later climb is measured from the new floor.
  private diffIncome(next: MatchState): void {
    const idx = this.viewerPlayerIndex(next);
    if (idx === null) return;
    const incomeNow = next.players[idx].signalIncomePerSec;
    // On the very first call, silently adopt the current income as the
    // baseline so the 0→base jump on tick 1 never triggers the voice line.
    if (!this.viewerIncomeSeeded) {
      this.viewerIncomeMark = incomeNow;
      this.viewerIncomeSeeded = true;
      return;
    }
    if (incomeNow - this.viewerIncomeMark >= VOICE_INCOME_DELTA_THRESHOLD) {
      this.voice?.playSignalIncomeIncreased();
      this.viewerIncomeMark = incomeNow;
    } else if (incomeNow < this.viewerIncomeMark) {
      // Income fell — track the new floor so a later recovery climb is
      // measured against where we actually landed, not the historical peak.
      this.viewerIncomeMark = incomeNow;
    }
  }

  // Threat heuristics based on the visible snapshot. We deliberately don't
  // invent wave logic — these are read-only sums over the data that's
  // already there. Both checks share enemy squad iteration so we walk the
  // table once per snapshot.
  private checkThreatHeuristics(snap: MatchState): void {
    let approachingCore = false;
    let pushNearRelays = 0;
    const viewerCore = this.firstViewerCore(snap);
    const viewerRelays: Array<{ x: number; y: number }> = [];
    for (const n of Object.values(snap.controlNodes)) {
      if (n.ownerId === this.viewerId) viewerRelays.push(n.pos);
    }
    for (const sq of Object.values(snap.squads)) {
      if (sq.ownerId === this.viewerId) continue;
      if (viewerCore !== null) {
        const dx = sq.pos.x - viewerCore.pos.x;
        const dy = sq.pos.y - viewerCore.pos.y;
        if (dx * dx + dy * dy <= HOSTILES_APPROACHING_CORE_RADIUS * HOSTILES_APPROACHING_CORE_RADIUS) {
          approachingCore = true;
        }
      }
      for (const rp of viewerRelays) {
        const dx = sq.pos.x - rp.x;
        const dy = sq.pos.y - rp.y;
        if (dx * dx + dy * dy <= ENEMY_PUSH_RELAY_RADIUS * ENEMY_PUSH_RELAY_RADIUS) {
          pushNearRelays += 1;
          break;
        }
      }
    }
    if (approachingCore) {
      this.voice?.playHostilesApproachingCore();
      this.lastPressureAt = performance.now();
    }
    if (pushNearRelays >= ENEMY_PUSH_THRESHOLD_COUNT) {
      this.voice?.playEnemyPushIncoming();
      this.lastPressureAt = performance.now();
    }
    // Also keep the one-shot HOSTILE_SIGNAL_DETECTED gate honest — if the
    // very first enemy showed up mid-match (e.g. an opening scout finally
    // crossed the mid line) we want to land the cue then too. Voice's
    // one-shot flag prevents repeats.
    if (approachingCore || pushNearRelays > 0) {
      this.voice?.playHostileSignalDetected();
    }
  }

  // Defense-line holding: fires after a stretch of calm following pressure.
  // The Voice layer additionally requires that DEFENSE_WEAKENING has fired
  // at least once this match, so an opening with no early skirmish doesn't
  // get a random "holding" flavour line.
  private checkDefenseLineHolding(): void {
    if (this.lastPressureAt === 0) return;
    const calmFor = performance.now() - this.lastPressureAt;
    // 30 s of no fresh pressure events before we even consider firing.
    if (calmFor < 30000) return;
    this.voice?.playDefenseLineHolding();
  }

  // Phase Shield activation / expiry — derive from the snapshot's
  // shieldBubbles map. Diff is plain set-add/set-remove; no marker on the
  // bubble distinguishes "expired" from "absorbed away", which is fine — we
  // play the same "shield collapse" cue in either case.
  private diffShieldBubbles(prev: MatchState, next: MatchState): void {
    for (const idStr of Object.keys(next.shieldBubbles)) {
      const id = idStr as unknown as EntityId;
      if (prev.shieldBubbles[id] === undefined) this.play('ability_shield_on');
    }
    for (const idStr of Object.keys(prev.shieldBubbles)) {
      const id = idStr as unknown as EntityId;
      if (next.shieldBubbles[id] === undefined) this.play('ability_shield_off');
    }
  }

  // Mark Target propagation. The simulation flags a marked squad via a
  // boolean / tick on the Squad entity; we just need to detect the leading
  // edge. If the squad's `markedByPlayerId` field isn't present on the type
  // in a given build, the optional read silently skips the cue rather than
  // throwing.
  //
  // (Wired inside `diffSquads` would be cleaner, but Mark fires while squads
  // are alive — keeping it as its own method keeps the death/spawn loop tidy.)
  // Currently not wired into onSnapshot because the cue is best produced by
  // the casting client immediately, not derived from the snapshot — Mark fires
  // from `InputRouter.handleMarkClick` via `playAbilityMark()` below.

  playAbilityMark(): void {
    this.play('ability_mark_target');
  }

  private checkTimerThresholds(snap: MatchState): void {
    const remaining = snap.matchLengthSec - snap.matchSeconds;
    if (!this.timerWarningEmitted && remaining <= TIMER_WARNING_REMAINING_SEC && remaining > 0) {
      this.timerWarningEmitted = true;
      this.play('timer_warning');
    }
    if (!this.finalCountdownEmitted && remaining <= FINAL_COUNTDOWN_REMAINING_SEC && remaining > 0) {
      this.finalCountdownEmitted = true;
      this.play('final_countdown');
    }
  }

  // Multi-kill streak: stacks kills landed inside the window and fires the
  // best-matching announce when the window closes. Solo kill outside any
  // streak → announce_kill; 2/3/4 → multikill_N; 5+ → multikill_god.
  private registerViewerKill(): void {
    const now = performance.now();
    if (this.pendingKillCount === 0 || now - this.killWindowAt > MULTIKILL_WINDOW_MS) {
      this.pendingKillCount = 0;
      this.killWindowAt = now;
    }
    this.pendingKillCount += 1;
    if (this.killWindowTimer !== null) clearTimeout(this.killWindowTimer);
    this.killWindowTimer = setTimeout(() => this.flushKillStreak(), MULTIKILL_WINDOW_MS);
  }

  private flushKillStreak(): void {
    const count = this.pendingKillCount;
    this.pendingKillCount = 0;
    this.killWindowAt = 0;
    this.killWindowTimer = null;
    if (count <= 0) return;
    // V1.8 defensive: between scheduling the timer and it firing, detach()
    // might have been called as part of a rematch teardown. Both unsubs
    // being null is the signal we're detached; bailing avoids playing into
    // a torn-down AudioBus or surfacing a stale streak in the next match.
    if (this.unsubSnap === null && this.unsubLife === null) return;
    if (count === 1) this.play('announce_kill');
    else if (count === 2) this.play('multikill_2');
    else if (count === 3) this.play('multikill_3');
    else if (count === 4) this.play('multikill_4');
    else this.play('multikill_god');
  }

  private play(name: string): void {
    if (this.muted) return;
    const now = performance.now();
    const last = this.lastPlayedAt.get(name) ?? -Infinity;
    if (now - last < PER_NAME_THROTTLE_MS) return;
    this.lastPlayedAt.set(name, now);
    const override = SFX_VOLUME_OVERRIDES[name];
    if (isCombatSfx(name)) {
      // Combat cues fire dozens of times per match; ±5% pitch + ±10% volume
      // randomization breaks up the "same sample on a loop" feel without
      // touching identity. UI/event stings deliberately skip this so a button
      // click sounds the same every time the player taps it.
      //
      // Fire cues are additionally attenuated to 0.35 — at full level they
      // drown out hits, voice, and music when several squads engage at once.
      // Other combat cues (hits, squad deaths) attenuate to 0.7 so impacts
      // and explosions don't bite the player during heavy engagements.
      const volume = override ?? (name.endsWith('_fire') ? 0.35 : 0.7);
      void this.bus.playSfx(name, { volume, pitchRange: 0.1, volumeJitter: 0.2 });
    } else if (override !== undefined) {
      void this.bus.playSfx(name, { volume: override });
    } else {
      void this.bus.playSfx(name);
    }
  }

  // Recent-death helpers used by the multi-squad-loss announcer and the
  // defense-line-weakening heuristic. We keep timestamps in a plain array
  // sized to the small handful of deaths that can land inside the window;
  // there's no need for a fancier ring buffer for this scale.
  private recordViewerDeath(now: number): void {
    this.recentViewerDeaths.push(now);
    // Trim from the front while entries fall out of the window.
    const cutoff = now - RECENT_DEATH_WINDOW_MS;
    while (this.recentViewerDeaths.length > 0 && this.recentViewerDeaths[0]! < cutoff) {
      this.recentViewerDeaths.shift();
    }
  }

  private countRecentDeaths(now: number): number {
    const cutoff = now - RECENT_DEATH_WINDOW_MS;
    let n = 0;
    for (const t of this.recentViewerDeaths) {
      if (t >= cutoff) n += 1;
    }
    return n;
  }

  private viewerPlayerIndex(snap: MatchState): 0 | 1 | null {
    if (snap.players[0].id === this.viewerId) return 0;
    if (snap.players[1].id === this.viewerId) return 1;
    return null;
  }

  private firstViewerCore(snap: MatchState): Core | null {
    for (const c of Object.values(snap.cores)) {
      if (c.ownerId === this.viewerId) return c;
    }
    return null;
  }

  private hasVisibleEnemy(snap: MatchState): boolean {
    for (const sq of Object.values(snap.squads)) {
      if (sq.ownerId !== this.viewerId) return true;
    }
    return false;
  }
}
